import { useState, useEffect, useCallback, useRef } from 'react'
import type { CastProject, Shot, JobStatus, SequenceJobStatus, MergeJobStatus, TransitionConfig } from '../types'
import { mergeVideos } from '../lib/merge'

export function useCast() {
  const [projects, setProjects] = useState<CastProject[]>([])
  const [activeProject, setActiveProject] = useState<string>('')
  const [shots, setShots] = useState<Shot[]>([])
  const [grouped, setGrouped] = useState<Record<string, Shot[]>>({})
  const [jobStatus, setJobStatus] = useState<Record<string, JobStatus>>({})
  const [sequenceJobs, setSequenceJobs] = useState<Record<string, SequenceJobStatus>>({})
  const [mergeJobs, setMergeJobs] = useState<Record<string, MergeJobStatus>>({})
  const [hasApiKey, setHasApiKey] = useState(false)
  const [loading, setLoading] = useState(true)
  const [resolution, setResolution] = useState('1080p')
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    Promise.all([
      fetch('/api/cast/projects').then((r) => r.json()),
      fetch('/api/cast/config').then((r) => r.json()),
    ]).then(([projs, config]) => {
      setProjects(projs)
      setHasApiKey(config.hasApiKey)
      if (projs.length > 0) {
        setActiveProject(projs[0].id)
      }
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [])

  const loadShots = useCallback(async (projectId: string) => {
    if (!projectId) return
    setLoading(true)
    try {
      const res = await fetch(`/api/cast/shots?project=${projectId}`)
      const data = await res.json()
      setShots(data.shots || [])
      setGrouped(data.grouped || {})
    } catch (err) {
      console.error('Failed to load shots:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (activeProject) loadShots(activeProject)
  }, [activeProject, loadShots])

  const fetchStatus = useCallback(async () => {
    const res = await fetch(`/api/cast/status?_t=${Date.now()}`)
    return await res.json()
  }, [])

  const applyStatus = useCallback((status: Record<string, any>) => {
    let anyActive = false
    const updates: Record<string, JobStatus> = {}
    const seqUpdates: Record<string, SequenceJobStatus> = {}
    const mergeUpdates: Record<string, MergeJobStatus> = {}

    for (const [key, s] of Object.entries(status) as [string, any][]) {
      if (s.type === 'merged') {
        mergeUpdates[key] = {
          state: 'done',
          progress: 100,
          log: 'Complete',
          error: null,
          file: s.file,
        }
      } else if (s.type === 'sequence') {
        seqUpdates[key] = s as SequenceJobStatus
        if (s.state === 'generating' || s.state === 'extending') anyActive = true
      } else {
        updates[key] = s as JobStatus
        if (s.state === 'generating') anyActive = true
      }
    }

    setJobStatus((prev) => ({ ...prev, ...updates }))
    setSequenceJobs((prev) => ({ ...prev, ...seqUpdates }))
    setMergeJobs((prev) => ({ ...prev, ...mergeUpdates }))
    return anyActive
  }, [])

  const loadExistingStatus = useCallback(async () => {
    try {
      const status = await fetchStatus()
      const anyActive = applyStatus(status)
      if (anyActive) startPolling()
    } catch {}
  }, [fetchStatus, applyStatus])

  useEffect(() => {
    loadExistingStatus()
  }, [loadExistingStatus])

  const fetchAndApplyRef = useRef({ fetchStatus, applyStatus })
  fetchAndApplyRef.current = { fetchStatus, applyStatus }

  const startPolling = useCallback(() => {
    if (pollRef.current) return
    pollRef.current = setInterval(async () => {
      try {
        const status = await fetchAndApplyRef.current.fetchStatus()
        const anyActive = fetchAndApplyRef.current.applyStatus(status)

        if (!anyActive && pollRef.current) {
          clearInterval(pollRef.current)
          pollRef.current = null
        }
      } catch {}
    }, 3000)
  }, [])

  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [])

  const generateOne = useCallback(async (
    shot: Shot,
    promptOverride?: string,
    referenceAssetIds?: string[],
  ) => {
    const key = `${activeProject}/${shot.videoId}/${shot.shotId}`
    setJobStatus((prev) => ({
      ...prev,
      [key]: { state: 'generating', elapsed: 0, error: null, file: null, logs: [] },
    }))

    startPolling()

    try {
      const payload: Record<string, unknown> = {
        projectId: activeProject,
        videoId: shot.videoId,
        shotId: shot.shotId,
        prompt: promptOverride ?? shot.prompt,
        aspectRatio: shot.aspectRatio,
      }
      if (referenceAssetIds?.length) {
        payload.referenceAssetIds = referenceAssetIds
      }

      const res = await fetch('/api/cast/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const data = await res.json()
      if (data.error) {
        setJobStatus((prev) => ({
          ...prev,
          [key]: { state: 'failed', elapsed: 0, error: data.error, file: null, logs: [] },
        }))
      }
    } catch (err: any) {
      setJobStatus((prev) => ({
        ...prev,
        [key]: { state: 'failed', elapsed: 0, error: err.message, file: null, logs: [] },
      }))
    }
  }, [activeProject, startPolling])

  const generateSequence = useCallback(async (
    group: string,
    groupShots: Shot[],
    opts?: { characterAssetIds?: string[]; sceneAssetIds?: string[] },
  ) => {
    if (!groupShots.length) return
    const videoId = groupShots[0].videoId
    const seqKey = `${activeProject}/${videoId}/${group}`

    setSequenceJobs((prev) => ({
      ...prev,
      [seqKey]: {
        state: 'generating',
        currentStep: 1,
        totalSteps: groupShots.length,
        error: null,
        file: null,
        segments: [],
      },
    }))

    startPolling()

    try {
      const prompts = groupShots
        .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
        .map((s) => {
          let userPrompt: string | null = null
          try {
            const raw = localStorage.getItem(`cast-prompt::${activeProject}/${s.videoId}/${s.shotId}`)
            if (raw) {
              const stored = JSON.parse(raw)
              if (stored.base === s.prompt && stored.edit) userPrompt = stored.edit
            }
          } catch {}
          return {
            id: s.shotId,
            label: s.label,
            prompt: userPrompt || s.prompt,
            sequence: s.sequence ?? 0,
          }
        })

      const res = await fetch('/api/cast/sequences/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: activeProject,
          videoId,
          group,
          prompts,
          aspectRatio: groupShots[0].aspectRatio,
          referenceAssetIds: [
            ...(opts?.characterAssetIds || []),
            ...(opts?.sceneAssetIds || []),
          ].length ? [
            ...(opts?.characterAssetIds || []),
            ...(opts?.sceneAssetIds || []),
          ] : undefined,
        }),
      })
      const data = await res.json()
      if (data.error) {
        setSequenceJobs((prev) => ({
          ...prev,
          [seqKey]: {
            state: 'failed',
            currentStep: 0,
            totalSteps: groupShots.length,
            error: data.error,
            file: null,
            segments: [],
          },
        }))
      }
    } catch (err: any) {
      setSequenceJobs((prev) => ({
        ...prev,
        [seqKey]: {
          state: 'failed',
          currentStep: 0,
          totalSteps: groupShots.length,
          error: err.message,
          file: null,
          segments: [],
        },
      }))
    }
  }, [activeProject, startPolling])

  const mergeGroup = useCallback(async (
    group: string,
    groupShots: Shot[],
    transitions: TransitionConfig[],
  ) => {
    const videoId = groupShots[0]?.videoId
    if (!videoId) return
    const mergeKey = `${activeProject}/${videoId}/${group}-merged`

    const setMerge = (patch: Partial<MergeJobStatus>) =>
      setMergeJobs((prev) => ({
        ...prev,
        [mergeKey]: {
          ...(prev[mergeKey] ?? { state: 'idle' as const, progress: 0, log: '', error: null, file: null }),
          ...patch,
        },
      }))

    setMerge({ state: 'loading', progress: 0, log: 'Starting…', error: null, file: null })

    try {
      // Collect video URLs from jobStatus in sequence order
      const ordered = [...groupShots].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
      const inputs = ordered.map((s) => {
        const key = `${activeProject}/${s.videoId}/${s.shotId}`
        const file = jobStatus[key]?.file
        if (!file) throw new Error(`Shot "${s.label}" has not been generated yet`)
        return { url: file, label: s.label }
      })

      // Run client-side merge via ffmpeg.wasm
      setMerge({ state: 'merging' })
      const merged = await mergeVideos({
        inputs,
        transitions,
        onLog: (log) => setMerge({ log }),
        onProgress: (progress) => setMerge({ progress }),
      })

      // Upload to Supabase via backend
      setMerge({ state: 'uploading', progress: 100, log: 'Uploading to storage…' })
      const transitionLabel = transitions[0]?.type ?? 'dissolve'
      const res = await fetch(
        `/api/cast/merge/save?projectId=${encodeURIComponent(activeProject)}&videoId=${encodeURIComponent(videoId)}&group=${encodeURIComponent(group)}&transition=${encodeURIComponent(transitionLabel)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'video/mp4' },
          body: merged.buffer.slice(merged.byteOffset, merged.byteOffset + merged.byteLength) as ArrayBuffer,
        },
      )
      const data = await res.json() as { file?: string; error?: string }
      if (!res.ok || data.error) throw new Error(data.error || `Upload failed: ${res.status}`)

      setMerge({ state: 'done', progress: 100, log: 'Complete', file: data.file ?? null })
    } catch (err: any) {
      setMerge({ state: 'failed', error: err.message ?? 'Merge failed' })
    }
  }, [activeProject, jobStatus])

  const clearMerge = useCallback(async (mergeKey: string) => {
    try {
      await fetch(`/api/cast/status/${encodeURIComponent(mergeKey)}`, { method: 'DELETE' })
    } catch {}
    setMergeJobs((prev) => {
      const copy = { ...prev }
      delete copy[mergeKey]
      return copy
    })
  }, [])

  const doneCount = Object.values(jobStatus).filter((j) => j.state === 'done').length

  const dismissSequenceError = useCallback((seqKey: string) => {
    setSequenceJobs((prev) => {
      const copy = { ...prev }
      delete copy[seqKey]
      return copy
    })
  }, [])

  const clearSequence = useCallback(async (seqKey: string) => {
    try {
      await fetch(`/api/cast/status/${encodeURIComponent(seqKey)}`, { method: 'DELETE' })
    } catch {}
    setSequenceJobs((prev) => {
      const copy = { ...prev }
      delete copy[seqKey]
      return copy
    })
  }, [])

  return {
    projects,
    activeProject,
    setActiveProject,
    shots,
    grouped,
    jobStatus,
    sequenceJobs,
    mergeJobs,
    hasApiKey,
    loading,
    resolution,
    setResolution,
    generateOne,
    generateSequence,
    dismissSequenceError,
    clearSequence,
    mergeGroup,
    clearMerge,
    doneCount,
    refresh: () => loadShots(activeProject),
  }
}
